import React from "react";

// renderNarrative.jsx - the pure @figure token renderer (SP6 Task 2).
//
// A narrative string (run.narrative's free-text fields, or a driver annotation's cause/
// expected/owner/action) may contain `{{fig:KEY}}` tokens referencing a METRIC_CATALOG key
// (reportMetrics.js). renderNarrative resolves those tokens against a `metrics` map (the
// useReportMetrics.js hook's output: `{ [key]: {key,label,value} }`) into a live `<span
// className="a4-fig">` for both the interactive preview and the printed/exported PDF.
//
// Hard backward-compat constraint (see the SP6 plan's print-parity note): a narrative with NO
// tokens must render EXACTLY as it did pre-SP6 - byte-identical DOM, so every report authored
// before @figures existed (and every report that never uses them) is untouched. That's why a
// token-free string returns VERBATIM (the plain string itself, not an array/fragment wrapping
// it) rather than always going through the split/map path.

// FIG_TOKEN - one well-formed {{fig:KEY}} token; KEY matches the METRIC_CATALOG key charset
// (lowercase letters/digits/underscore). Not anchored (no ^/$) so it matches anywhere inside a
// longer sentence. A malformed token missing its closing "}}" (e.g. "a {{fig:x") simply never
// matches - it falls through as literal text with no special-casing.
const FIG_TOKEN = /\{\{fig:([a-z0-9_]+)\}\}/;

/**
 * hasFigToken(text) -> bool - true iff `text` is a string containing at least one well-formed
 * {{fig:KEY}} token. Used by the narrative blocks to gate `BaseBlock`'s `isLoading` on
 * `metricsLoading` ONLY when the block's rendered narrative actually references a metric; a
 * token-free block never waits on metrics (unchanged `isLoading={false}`).
 */
export function hasFigToken(text) {
  return typeof text === "string" && FIG_TOKEN.test(text);
}

/**
 * renderNarrative(text, metrics) -> ReactNode - splits `text` on {{fig:KEY}} tokens: text runs
 * render verbatim, each token renders `metrics?.[key]?.value` (or "—" when `metrics` is absent,
 * or the key is unknown/unresolved) inside a `<span className="a4-fig">`, keyed by occurrence
 * index. Never throws.
 *
 * - `text` null/undefined -> returned as-is (callers already doing `{summary || "—"}` /
 *   `{decisionRequired && <p>...}` around this are unaffected).
 * - `text` token-free -> returned VERBATIM (the plain string) - see the file doc comment above.
 * - `metrics` undefined/missing a key -> that token renders "—", no throw.
 */
export function renderNarrative(text, metrics) {
  if (typeof text !== "string") return text;
  if (!hasFigToken(text)) return text;

  const re = /\{\{fig:([a-z0-9_]+)\}\}/g;
  const parts = [];
  let lastIndex = 0;
  let match;
  let i = 0;

  while ((match = re.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    const key = match[1];
    const value = metrics?.[key]?.value ?? "—";
    parts.push(
      // eslint-disable-next-line react/no-array-index-key -- token occurrences within one
      // narrative string have no natural stable identity beyond their position.
      <span className="a4-fig" key={i}>
        {value}
      </span>
    );
    i += 1;
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts;
}

export default renderNarrative;
